/**
 * Per-pillar color classes for TrybeUP's three core metrics:
 *
 *   integrity  — amber  (the brand anchor; honesty + showing up)
 *   momentum   — green  (forward motion; reps compounding)
 *   clarity    — blue   (self-awareness; reflection signals)
 *
 * Source of truth: `lib/design-tokens.ts` metrics map. The hex values
 * below are the spec's DARK tokens, applied via Tailwind arbitrary
 * values so the JIT can statically extract them at build time. When
 * light mode ships, these will need a per-theme split — most likely
 * via a CSS-var indirection (`var(--metric-integrity)` etc.) instead
 * of literal hex, so the same class string works in both modes.
 *
 * Why a shared module: pre-2026-05-31 every pillar across the app
 * (PillarBars, MissionRitual, MissionCard, DaySummary, MissionPicker,
 * ActiveMissionBar) rendered as amber regardless of which metric it
 * represented — making the trio visually indistinguishable. This map
 * gives each pillar its own identity, consistently, in one place.
 */
export type PillarKey = "integrity" | "momentum" | "clarity";

export const PILLAR_COLOR: Record<PillarKey, {
  /** Soft tinted background for icon circles, chips. ~15% alpha. */
  iconBg: string;
  /** Stronger tint for hover / active states. ~25% alpha. */
  iconBgStrong: string;
  /** Icon stroke color. */
  iconText: string;
  /** Label / accent text color. */
  text: string;
  /** Solid fill — progress bars, badges. */
  barBg: string;
  /** Border accent. */
  border: string;
  /** Softer border (for resting card chrome). */
  borderSoft: string;
}> = {
  // Integrity = amber, intentionally, in EVERY theme including Calm.
  // Product decision 2026-06-01: amber on Integrity (and the To Do tab
  // in MyDay.tsx) "sends a good message of importance and execution."
  // These use the explicit brand hex `#e59f25` rather than `amber-*`
  // Tailwind utilities, so the Calm-mode amber→peach overrides in
  // index.css (:root.calm-mode) deliberately do NOT catch them. Keep
  // it hex-based — do not refactor to amber-* utilities or a token, or
  // Calm will repaint it peach and the importance signal is lost.
  integrity: {
    iconBg:       "bg-[#e59f25]/15",
    iconBgStrong: "bg-[#e59f25]/25",
    iconText:     "text-[#e59f25]",
    text:         "text-[#e59f25]",
    barBg:        "bg-[#e59f25]",
    border:       "border-[#e59f25]/40",
    borderSoft:   "border-[#e59f25]/25",
  },
  momentum: {
    iconBg:       "bg-[#27c463]/15",
    iconBgStrong: "bg-[#27c463]/25",
    iconText:     "text-[#27c463]",
    text:         "text-[#27c463]",
    barBg:        "bg-[#27c463]",
    border:       "border-[#27c463]/40",
    borderSoft:   "border-[#27c463]/25",
  },
  clarity: {
    iconBg:       "bg-[#5cb0e6]/15",
    iconBgStrong: "bg-[#5cb0e6]/25",
    iconText:     "text-[#5cb0e6]",
    text:         "text-[#5cb0e6]",
    barBg:        "bg-[#5cb0e6]",
    border:       "border-[#5cb0e6]/40",
    borderSoft:   "border-[#5cb0e6]/25",
  },
};
